import { Link } from "react-router-dom";
import { StatusChip } from "@/components/app/StatusChip";
import { ROW_STATUS } from "@/lib/intake";
import { cn } from "@/lib/utils";

const CHIP = { new: "Ready", duplicate: "Needs review", failed: "Failed", queued: "Queued", processing: "Processing" };
const RESULT = { imported: "Imported", merged: "Merged", skipped: "Skipped", failed: "Failed" };
const DECISIONS = { new: [["import", "Import"], ["skip", "Skip"]], duplicate: [["merge", "Merge"], ["import", "Import anyway"], ["skip", "Skip"]] };

function Decision({ row, onDecide, disabled }) {
  const opts = DECISIONS[row.status];
  if (!opts) return <span className="text-xs text-ink-muted">—</span>;
  return (
    <div className="inline-flex rounded-[5px] border border-line bg-white p-0.5" role="radiogroup" aria-label={`Action for ${row.label}`}>
      {opts.map(([k, l]) => (
        <button key={k} type="button" role="radio" aria-checked={row.decision === k} disabled={disabled} onClick={() => onDecide(row, k)}
          data-testid={`row-${row.row_no}-${k}`}
          className={cn("rounded-md px-2.5 py-1 text-xs transition-colors duration-150 disabled:opacity-60", row.decision === k ? "bg-brand text-white" : "text-ink-secondary hover:bg-subtle hover:text-ink")}>{l}</button>
      ))}
    </div>
  );
}

function Detail({ r }) {
  if (r.status === "failed") return <span className="text-risk" data-testid={`row-${r.row_no}-reason`}>{r.reason}</span>;
  if (r.status === "duplicate") {
    return (
      <span data-testid={`row-${r.row_no}-reason`}>
        {r.dup_reasons.join(", ")}{r.duplicate && <> · matches <Link to={`/app/candidates/${r.duplicate.id}/resume`} className="text-brand hover:underline" target="_blank">{r.duplicate.name}</Link></>}
      </span>
    );
  }
  return <span>{[r.data?.current_title, r.flags?.includes("resume not fetched") && "Resume not fetched"].filter(Boolean).join(" · ") || ""}</span>;
}

export function ImportRowsTable({ rows, committed, onDecide, busy }) {
  return (
    <div className="panel overflow-x-auto">
      <table className="w-full text-sm" data-testid="import-rows-table">
        <thead className="bg-subtle text-left text-xs text-ink-muted">
          <tr><th className="px-4 py-3 font-medium">#</th><th className="px-4 py-3 font-medium">File / row</th><th className="px-4 py-3 font-medium">Candidate</th>
            <th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium">Details</th><th className="px-4 py-3 font-medium">Job</th>
            <th className="px-4 py-3 text-right font-medium">{committed ? "Result" : "Action"}</th></tr>
        </thead>
        <tbody className="divide-y divide-line">
          {rows.map((r) => (
            <tr key={r.id} data-testid={`import-row-${r.row_no}`} className="transition-colors duration-150 hover:bg-subtle/50">
              <td className="px-4 py-2.5 text-xs tabular-nums text-ink-muted">{r.row_no}</td>
              <td className="max-w-[220px] truncate px-4 py-2.5 text-ink-secondary" title={r.label}>{r.label}</td>
              <td className="px-4 py-2.5 font-medium text-ink">{r.data?.name || r.data?.email || "—"}</td>
              <td className="px-4 py-2.5"><StatusChip status={CHIP[r.status]} label={ROW_STATUS[r.status]} testId={`row-${r.row_no}-status`} /></td>
              <td className="max-w-[320px] px-4 py-2.5 text-xs text-ink-secondary"><Detail r={r} /></td>
              <td className="px-4 py-2.5 text-xs text-ink-secondary">{r.job_title || "Talent pool"}</td>
              <td className="px-4 py-2.5 text-right">
                {committed ? <span className="text-xs font-medium" data-testid={`row-${r.row_no}-result`}>{RESULT[r.result] || "—"}{r.candidate_id && <Link to={`/app/candidates/${r.candidate_id}/resume`} className="ml-2 text-brand hover:underline">Open</Link>}</span>
                  : <Decision row={r} onDecide={onDecide} disabled={busy} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
